/* IndexedDB layer (idb) — offline-first store for jobs, clients, documents,
 * rate schedules, and a sync queue. Every offline mutation goes through these
 * helpers, which also append a sync intent so we can replay on reconnect. */
import { openDB } from "idb";

const DB_NAME = "rom-engine";
const DB_VERSION = 2;

export const STORES = {
    jobs: "jobs",
    clients: "clients",
    documents: "documents",
    rate_schedules: "rate_schedules",
    sync_queue: "sync_queue",
    kv: "kv", // small key/value store (current user, active schedule pointer)
    voice_chunks: "voice_chunks", // durable offline voice-dictation audio, keyed by sessionId
};

let _dbPromise = null;

export function getDB() {
    if (!_dbPromise) {
        _dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                for (const name of Object.values(STORES)) {
                    if (!db.objectStoreNames.contains(name)) {
                        const keyPath = name === "sync_queue" || name === "kv" ? "key" : "id";
                        db.createObjectStore(name, { keyPath });
                    }
                }
            },
        });
    }
    return _dbPromise;
}

export async function putAll(store, items) {
    if (!items || !items.length) return;
    const db = await getDB();
    const tx = db.transaction(store, "readwrite");
    for (const item of items) await tx.store.put(item);
    await tx.done;
}

export async function put(store, item) {
    const db = await getDB();
    await db.put(store, item);
}

export async function getAll(store) {
    const db = await getDB();
    return db.getAll(store);
}

export async function getOne(store, id) {
    const db = await getDB();
    return db.get(store, id);
}

export async function removeOne(store, id) {
    const db = await getDB();
    await db.delete(store, id);
}

export async function kvSet(key, value) {
    await put(STORES.kv, { key, value });
}

export async function kvGet(key) {
    const r = await getOne(STORES.kv, key);
    return r ? r.value : null;
}

/* Sync queue */
export async function queueSync(kind, payload) {
    const item = {
        key: `${kind}:${payload.id}:${Date.now()}`,
        kind, // 'jobs' | 'clients' | 'documents'
        payload,
        created_at: new Date().toISOString(),
    };
    await put(STORES.sync_queue, item);
}

export async function clearQueueItems(keys) {
    const db = await getDB();
    const tx = db.transaction(STORES.sync_queue, "readwrite");
    for (const k of keys) await tx.store.delete(k);
    await tx.done;
}

export async function getQueue() {
    return getAll(STORES.sync_queue);
}

/* Voice-dictation offline durability — a chunk is persisted here the moment
 * it fails to transcribe while offline, so it survives an app close/reload/
 * crash and auto-sends once the estimator is back online (even if they
 * reopen the same ROM job hours later). Keyed by `${sessionId}:${index}`. */
export async function saveVoiceChunk(item) {
    await put(STORES.voice_chunks, item);
}

export async function getVoiceChunksBySession(sessionId) {
    const all = await getAll(STORES.voice_chunks);
    return all.filter((c) => c.sessionId === sessionId).sort((a, b) => a.created_at - b.created_at);
}

export async function deleteVoiceChunk(id) {
    await removeOne(STORES.voice_chunks, id);
}
